import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    TableSortLabel,
    Snackbar,
    Alert,
    TablePagination,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from "@mui/material";
import {
    Add as AddIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Work as WorkIcon,
    Print as PrintIcon
} from "@mui/icons-material";
import { useAuth } from '../../hooks/useAuth.js';
import api from '../../services/api.js';
import Preloader from '../../components/Preloader';
import { formatarTelefone, formatarCPF, formatarCNPJ } from '../../utils/formatadores.js';
import CadastroRepresentante from './CadastroRepresentante';

export default function ListagemRepresentantes() {
    const [representantes, setRepresentantes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Controle do Dialog de Cadastro
    const [openCadastro, setOpenCadastro] = useState(false);
    const [representanteEditarId, setRepresentanteEditarId] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [representanteVisualizar, setRepresentanteVisualizar] = useState(null);

    const [filtros, setFiltros] = useState({
        st_nome: "",
        st_cpf_cnpj: "",
    });

    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("st_nome");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        carregarRepresentantes();
    }, []);

    const carregarRepresentantes = async () => {
        setLoading(true);
        try {
            const response = await api.get('/representante'); // Endpoint no plural ou singular? Rota foi definida como /representante
            setRepresentantes(response.data.dados || []);
        } catch (error) {
            console.error('Erro ao carregar representantes:', error);
            setError('Erro ao carregar representantes');
        } finally {
            setLoading(false);
        }
    };

    const handleFiltroChange = (event) => {
        const { name, value } = event.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleOpenCadastro = (id = null) => {
        setRepresentanteEditarId(id);
        setOpenCadastro(true);
    };

    const handleCloseCadastro = () => {
        setOpenCadastro(false);
        setRepresentanteEditarId(null);
    };

    const handleSaveSuccess = (message) => {
        setSuccess(message);
        carregarRepresentantes();
        handleCloseCadastro();
    };

    const visualizarRepresentante = (rep) => {
        setRepresentanteVisualizar(rep);
        setOpenView(true);
    };

    const fecharVisualizacao = () => {
        setOpenView(false);
        setRepresentanteVisualizar(null);
    };

    const { user } = useAuth(); // Access user context

    const handlePrint = async (rep) => {
        try {
            const [templateRes, empresaRes] = await Promise.all([
                api.get('/contrato-template'),
                user?.id_empresa || user?.idempresa ? api.get(`/empresa/${user?.id_empresa || user?.idempresa}`) : Promise.resolve({ data: {} })
            ]);

            const templates = templateRes.data.dados || [];
            // Procurar template de Representante
            const template = templates.find(t => t.st_titulo.includes('Representante') || t.st_titulo.includes('Parceiro'));
            const empresaData = empresaRes.data?.dados || empresaRes.data || {};
            const logoUrl = empresaData.st_logotipo || '';

            if (!template) {
                alert('Modelo de contrato de representante não encontrado.');
                return;
            }

            let conteudo = template.st_conteudo_html;

            // Formatar Data Atual
            const hoje = new Date();
            const dia = String(hoje.getDate()).padStart(2, '0');
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const ano = hoje.getFullYear();
            const dataAtual = `${dia}/${mes}/${ano}`;

            // Helper para destacar campos
            const highlight = (text) => `<span class="highlight">${text}</span>`;

            // Helper para formatar data para BR
            const formatarDataBR = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            };

            // Substituir Placeholders - Dados Pessoais/Empresariais
            conteudo = conteudo.replace(/{{nome}}/g, highlight(rep.st_nome || ''));
            conteudo = conteudo.replace(/{{nome_fantasia}}/g, highlight(rep.st_nome_fantasia || ''));

            const docFormatado = rep.en_tipo_pessoa === 'PJ' ? formatarCNPJ(rep.st_cpf_cnpj) : formatarCPF(rep.st_cpf_cnpj);
            conteudo = conteudo.replace(/{{cpf}}/g, highlight(docFormatado || ''));
            conteudo = conteudo.replace(/{{cnpj}}/g, highlight(docFormatado || ''));

            conteudo = conteudo.replace(/{{rg_ie}}/g, highlight(rep.st_rg_ie || ''));
            conteudo = conteudo.replace(/{{nacionalidade}}/g, highlight(rep.st_nacionalidade || ''));
            conteudo = conteudo.replace(/{{estado_civil}}/g, highlight(rep.en_estado_civil || ''));

            // Endereço
            const enderecoCompleto = `${rep.st_logradouro || ''}, ${rep.st_numero || ''} ${rep.st_complemento ? '- ' + rep.st_complemento : ''} - ${rep.st_bairro || ''}`;
            conteudo = conteudo.replace(/{{endereco}}/g, highlight(enderecoCompleto));
            conteudo = conteudo.replace(/{{cep}}/g, highlight(rep.st_cep || ''));
            conteudo = conteudo.replace(/{{cidade}}/g, highlight(rep.st_cidade || ''));
            conteudo = conteudo.replace(/{{estado}}/g, highlight(rep.st_estado || ''));

            // Contatos
            conteudo = conteudo.replace(/{{email}}/g, highlight(rep.st_email || ''));
            conteudo = conteudo.replace(/{{telefone}}/g, highlight(formatarTelefone(rep.st_telefone) || ''));
            conteudo = conteudo.replace(/{{whatsapp}}/g, highlight(formatarTelefone(rep.st_whatsapp) || ''));

            // Dados Bancários
            conteudo = conteudo.replace(/{{banco}}/g, highlight(rep.st_banco || ''));
            conteudo = conteudo.replace(/{{agencia}}/g, highlight(rep.st_agencia || ''));
            conteudo = conteudo.replace(/{{conta}}/g, highlight(rep.st_conta || ''));
            conteudo = conteudo.replace(/{{pix}}/g, highlight(rep.st_chave_pix || ''));

            // Comercial
            conteudo = conteudo.replace(/{{comissao}}/g, highlight(rep.nu_comissao ? `${rep.nu_comissao}%` : ''));
            conteudo = conteudo.replace(/{{regiao}}/g, highlight(rep.st_regiao || ''));

            // Datas
            conteudo = conteudo.replace(/{{data_atual}}/g, highlight(dataAtual));
            conteudo = conteudo.replace(/{{dt_nascimento_abertura}}/g, highlight(formatarDataBR(rep.dt_nascimento_abertura)));

            const win = window.open('', '_blank');
            win.document.write(`
                <html>
                    <head>
                        <title>Contrato - ${rep.st_nome}</title>
                        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700&display=swap" rel="stylesheet">
                        <style>
                            @page {
                                size: A4;
                                margin: 0;
                            }
                            body { 
                                font-family: 'Lato', sans-serif;
                                font-size: 11pt;
                                color: #333;
                                line-height: 1.6;
                                margin: 0;
                                padding: 0.5cm;
                                box-sizing: border-box;
                            }

                            /* Container Principal com Borda e Cantos Arredondados */
                            .main-frame {
                                border: 2px solid #20ac9c; /* Use logo primary color */
                                border-radius: 20px;
                                padding: 40px 60px; /* Increased side padding */
                                min-height: 95vh;
                                position: relative;
                                margin: 0 auto;
                                box-sizing: border-box;
                            }

                            /* Header Setup */
                            .header {
                                display: flex;
                                align-items: flex-start;
                                justify-content: space-between;
                                margin-bottom: 5px; /* Reduced margin */
                                border-bottom: 3px solid #D4AF37; /* Gold Accent */
                                padding-bottom: 10px; /* Reduced padding */
                            }
                            .header-logo img {
                                height: 130px; /* Increased logo size */
                                object-fit: contain;
                            }
                            
                            /* Body Styling */
                            .contract-body {
                                text-align: justify;
                                white-space: pre-wrap;
                                overflow-wrap: break-word;
                                word-break: break-word;
                            }
                            .contract-body > *:first-child {
                                margin-top: 0;
                            }
                            
                            /* Gradient Bar Top/Bottom of Frame */
                            .frame-decoration-top {
                                height: 8px;
                                background: linear-gradient(90deg, #20ac9c 0%, #D4AF37 100%);
                                border-radius: 6px 6px 0 0;
                                margin: -40px -60px 30px -60px; /* Pull to edges */
                            }

                            /* Typography Enhancements */
                            strong, b {
                                color: #000;
                                font-weight: 700;
                            }
                            
                            /* Highlighted Fields */
                            .highlight {
                                font-weight: bold;
                                color: #20ac9c; /* Teal Text */
                                border-bottom: 1px dotted #333;
                                padding: 0 2px;
                            }

                            /* Signatures */
                            .signature-section {
                                margin-top: 50px;
                                break-inside: avoid;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="main-frame">
                            <div class="frame-decoration-top"></div>
                            
                            <div class="header">
                                <div class="header-logo">
                                    ${logoUrl ? `<img src="${logoUrl}" alt="Logo" />` : ''}
                                </div>
                            </div>

                            <div class="contract-body">
                                ${conteudo}
                            </div>
                        </div>

                        <script>
                            window.onload = function() { 
                                setTimeout(() => window.print(), 800); 
                            }
                        </script>
                    </body>
                </html>
            `);
            win.document.close();

        } catch (err) {
            console.error(err);
            setError("Erro ao gerar impressão.");
        }
    };

    // Filtragem e Ordenação
    const representantesFiltrados = representantes
        .filter((rep) => {
            const nomeMatch = !filtros.st_nome ||
                rep.st_nome?.toLowerCase().includes(filtros.st_nome.toLowerCase());
            const docMatch = !filtros.st_cpf_cnpj ||
                rep.st_cpf_cnpj?.includes(filtros.st_cpf_cnpj.replace(/\D/g, ''));

            return nomeMatch && docMatch;
        })
        .sort((a, b) => {
            let aValue = a[orderBy] || "";
            let bValue = b[orderBy] || "";

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (order === "asc") {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
                    {success}
                </Alert>
            </Snackbar>

            <Box sx={{
                margin: '0 1.5% 1.5% 1.5%',
                height: '90vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: { xs: 2, sm: 3 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2
                }}>
                    <Typography variant="h5" sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WorkIcon sx={{ fontSize: 'inherit', color: '#20ac9c' }} />
                        Representantes
                    </Typography>

                    <Button
                        variant="contained"
                        onClick={() => handleOpenCadastro()}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { xs: 'auto', sm: '200px' },
                            backgroundColor: '#20ac9c',
                            '&:hover': { backgroundColor: '#167a6f' }
                        }}
                    >
                        <AddIcon /> Novo Representante
                    </Button>
                </Box>

                {/* Tabela */}
                <TableContainer component={Paper} sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'auto',
                    height: { xs: 'calc(90vh - 140px)', sm: 'calc(90vh - 120px)' },
                    maxHeight: { xs: 'calc(90vh - 140px)', sm: 'calc(90vh - 120px)' },
                    width: '100%',
                    boxShadow: 2,
                    borderRadius: 2
                }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '30%' }}>
                                    <TableSortLabel
                                        active={orderBy === "st_nome"}
                                        direction={orderBy === "st_nome" ? order : "asc"}
                                        onClick={() => handleRequestSort("st_nome")}
                                    >
                                        Nome
                                    </TableSortLabel>
                                    <TextField
                                        name="st_nome"
                                        size="small"
                                        fullWidth
                                        value={filtros.st_nome}
                                        onChange={handleFiltroChange}
                                        placeholder="Filtrar por nome"
                                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <TableSortLabel
                                        active={orderBy === "st_cpf_cnpj"}
                                        direction={orderBy === "st_cpf_cnpj" ? order : "asc"}
                                        onClick={() => handleRequestSort("st_cpf_cnpj")}
                                    >
                                        CPF/CNPJ
                                    </TableSortLabel>
                                    <TextField
                                        name="st_cpf_cnpj"
                                        size="small"
                                        fullWidth
                                        value={filtros.st_cpf_cnpj}
                                        onChange={handleFiltroChange}
                                        placeholder="Filtrar doc"
                                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '15%' }}>Telefone</TableCell>
                                <TableCell sx={{ width: '15%' }}>Região</TableCell>
                                <TableCell sx={{ width: '10%', textAlign: 'center' }}>Status</TableCell>
                                <TableCell sx={{ width: '10%', textAlign: 'center' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Preloader message="Carregando representantes..." />
                                    </TableCell>
                                </TableRow>
                            ) : representantesFiltrados.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">Nenhum representante encontrado</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                representantesFiltrados
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((rep, index) => (
                                        <TableRow key={rep.id_representante}
                                            sx={{
                                                backgroundColor: (theme) => index % 2 === 0
                                                    ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
                                                    : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                        >
                                            <TableCell>{rep.st_nome}</TableCell>
                                            <TableCell>{rep.en_tipo_pessoa === 'PJ' ? formatarCNPJ(rep.st_cpf_cnpj) : formatarCPF(rep.st_cpf_cnpj)}</TableCell>
                                            <TableCell>{formatarTelefone(rep.st_telefone)}</TableCell>
                                            <TableCell>{rep.st_regiao || '-'}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={rep.en_status === 'ativo' ? 'Ativo' : (rep.en_status === 'suspenso' ? 'Suspenso' : 'Inativo')}
                                                    color={rep.en_status === 'ativo' ? 'success' : (rep.en_status === 'suspenso' ? 'warning' : 'default')}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Visualizar">
                                                    <IconButton size="small" onClick={() => visualizarRepresentante(rep)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Editar">
                                                    <IconButton size="small" onClick={() => handleOpenCadastro(rep.id_representante)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Imprimir Contrato">
                                                    <IconButton size="small" onClick={() => handlePrint(rep)}>
                                                        <PrintIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={representantesFiltrados.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Itens por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{ backgroundColor: (theme) => theme.palette.background.default, borderTop: 'none', width: '100%', marginTop: 0 }}
                />
            </Box>

            {/* Dialog Visualização */}
            <Dialog open={openView} onClose={fecharVisualizacao} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0', borderBottom: 1, borderColor: 'divider' }}>
                    Visualizar Representante
                    <IconButton onClick={fecharVisualizacao}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ padding: 3, mt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField fullWidth label="Nome" value={representanteVisualizar?.st_nome || ''} disabled />
                        <TextField fullWidth label="CPF/CNPJ" value={representanteVisualizar?.en_tipo_pessoa === 'PJ' ? formatarCNPJ(representanteVisualizar?.st_cpf_cnpj) : formatarCPF(representanteVisualizar?.st_cpf_cnpj)} disabled />
                        <TextField fullWidth label="Status" value={representanteVisualizar?.en_status || ''} disabled />
                        {/* Adicionar mais campos conforme necessidade de Quick View */}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0', borderTop: 1, borderColor: 'divider', padding: 2 }}>
                    <Button onClick={fecharVisualizacao} variant="contained">FECHAR</Button>
                </DialogActions>
            </Dialog>

            {/* Cadastro */}
            {openCadastro && (
                <CadastroRepresentante
                    open={openCadastro}
                    onClose={handleCloseCadastro}
                    representanteId={representanteEditarId}
                    onSuccess={handleSaveSuccess}
                />
            )}
        </Box>
    );
}
